import IconButton from "@material-ui/core/IconButton";
// import DeleteIcon from "@material-ui/icons/Delete";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { PAGINATE_BY } from "@saleor/config";
import { configurationMenuUrl } from "@saleor/configuration";
import CurrencyrateBulkDeleteDialog from "@saleor/currencyrate/components/CurrencyrateBulkDeleteDialog";
import CurrencyrateListPage from "@saleor/currencyrate/components/CurrencyrateListPage";
import EventDialog from "@saleor/currencyrate/components/EventDialog";
import {
  useCurrencyrateApplyRateMutation,
  useCurrencyrateBulkDeleteMutation
} from "@saleor/currencyrate/mutations";
import {
  useCurrencyrateEventQuery,
  useCurrencyrateListQuery
} from "@saleor/currencyrate/queries";
import { CurrencyrateApplyRateVariables } from "@saleor/currencyrate/types/CurrencyrateApplyRate";
import {
  currencyrateAddUrl,
  currencyrateListUrl,
  CurrencyrateListUrlDialog,
  CurrencyrateListUrlQueryParams,
  currencyrateUrl
} from "@saleor/currencyrate/urls";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { useIntl } from "react-intl";

import useBulkActions from "../../../hooks/useBulkActions";
import {
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  saveFilterTab
} from "./filters";
import {
  areFiltersApplied,
  getFilterTabs,
  getFilterVariables
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface CurrencyrateListProps {
  params: CurrencyrateListUrlQueryParams;
}

const CurrencyrateList: React.FC<CurrencyrateListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params)
    }),
    [params]
  );
  const { data, loading, refetch } = useCurrencyrateListQuery({
    variables: queryVariables
  });

  const [
    currencyrateBulkDelete,
    currencyrateBulkDeleteOpts
  ] = useCurrencyrateBulkDeleteMutation({
    onCompleted: data => {
      if (data.currencyrateBulkDelete.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Currencyrate successfully delete",
            description: "deleted multiple Currencyrate"
          })
        });
        reset();
        refetch();
      }
    }
  });
  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [openModal, closeModal] = createDialogActionHandlers<
    CurrencyrateListUrlDialog,
    CurrencyrateListUrlQueryParams
  >(navigate, currencyrateListUrl, params);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: currencyrateListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      currencyrateListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(currencyrateListUrl());
  };

  const [currencyrateApplyRate] = useCurrencyrateApplyRateMutation({
    onCompleted: data => {
      if (data.currencyrateApplyRate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "appllyrate"
          })
        });
        navigate(currencyrateListUrl());
      }
      reset();
      refetch();
    }
  });
  const onApply = currency => async e => {
    e.stopPropagation();
    const id = currency.id as CurrencyrateApplyRateVariables["id"];
    const result = await currencyrateApplyRate({
      variables: {
        id
      }
    });
    return result.data.currencyrateApplyRate.errors;
  };
  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.currencyrates.pageInfo),
    paginationState,
    params
  );

  const handleSort = createSortHandler(navigate, currencyrateListUrl, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");

  const eventdata = useCurrencyrateEventQuery({});

  return (
    <>
      <CurrencyrateListPage
        toatlCount = {maybe(() => data.currencyrates.totalCount,0)}
        currencyrate={maybe(() =>
          data.currencyrates.edges.map(edge => edge.node)
        )}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        disabled={loading || currencyrateBulkDeleteOpts.loading}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        isChecked={isSelected}
        onAdd={() => navigate(currencyrateAddUrl())}
        showEvent={() => openModal("event-dialog")}
        onAll={resetFilters}
        onBack={() => navigate(configurationMenuUrl)}
        onFilterChange={changeFilters}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onRowClick={id => () => navigate(currencyrateUrl(id))}
        onSearchChange={handleSearchChange}
        onSort={handleSort}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        pageInfo={pageInfo}
        selected={listElements.length}
        sort={getSortParams(params)}
        tabs={tabs.map(tab => tab.name)}
        toggle={toggle}
        onApply={onApply}
        toggleAll={toggleAll}
        toolbar={
          <IconButton
            color="primary"
            // onClick={() =>
            //   openModal("remove", {
            //     ids: listElements
            //   })
            // }
          >
            {/* <DeleteIcon /> */}
          </IconButton>
        }
      />
      <CurrencyrateBulkDeleteDialog
        confirmButtonState={currencyrateBulkDeleteOpts.status}
        open={params.action === "remove" && maybe(() => params.ids.length > 0)}
        onConfirm={() =>
          currencyrateBulkDelete({ variables: { ids: params.ids } })
        }
        onClose={closeModal}
        quantity={maybe(() => params.ids.length)}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
      <EventDialog
        data={eventdata.data}
        open={params.action === "event-dialog"}
        onClose={closeModal}
      />
    </>
  );
};
export default CurrencyrateList;
